import React, { useState } from 'react';
import { X, Building, Mail, Phone, MapPin, Users, Calendar, DollarSign, FileText, Activity, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import NotesTab from './shared/NotesTab';
import ActivityTab from './shared/ActivityTab';

const CompanyDetailModal = ({ company, isOpen, onClose, onEdit, canEdit = false }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [currentUser, setCurrentUser] = useState(null);

  React.useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  if (!company) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'suspended': 'bg-orange-100 text-orange-800',
      'terminated': 'bg-red-100 text-red-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="company-detail-modal">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Building className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-foreground" data-testid="company-name-header">
                {company.client_name || company.company_name || 'Unnamed Company'}
              </h2>
            </div>
            {company.industry && (
              <p className="text-sm text-muted-foreground">{company.industry}</p>
            )}
          </div>
          {company.status && (
            <Badge className={getStatusColor(company.status)} data-testid="company-status-badge">
              {company.status.toUpperCase()}
            </Badge>
          )}
        </div>

        <Separator className="mb-4" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mb-4" data-testid="company-detail-tabs">
            <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
              <FileText className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2" data-testid="tab-activity">
              <Activity className="h-4 w-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-2" data-testid="tab-notes">
              <MessageSquare className="h-4 w-4" />
              Notes
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6 mt-0" data-testid="details-content">
              {/* Contact Information */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {company.primary_contact_email && (
                    <div className="flex items-start gap-2 text-sm">
                      <Mail className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Email:</span>
                        <span className="ml-1 font-medium">{company.primary_contact_email}</span>
                      </div>
                    </div>
                  )}
                  {company.main_phone_number && (
                    <div className="flex items-start gap-2 text-sm">
                      <Phone className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Phone:</span>
                        <span className="ml-1 font-medium">{company.main_phone_number}</span>
                      </div>
                    </div>
                  )}
                  {company.company_website && (
                    <div className="flex items-start gap-2 text-sm">
                      <Building className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Website:</span>
                        <a href={company.company_website} target="_blank" rel="noopener noreferrer" className="ml-1 font-medium text-blue-600 hover:underline">
                          {company.company_website}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Address */}
              {(company.address_line_1 || company.city) && (
                <>
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90">Address</h3>
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        {company.address_line_1 && <div>{company.address_line_1}</div>}
                        {company.address_line_2 && <div>{company.address_line_2}</div>}
                        {company.city && (
                          <div>
                            {company.city}
                            {company.state && `, ${company.state}`}
                            {company.zip_code && ` ${company.zip_code}`}
                          </div>
                        )}
                        {company.country && <div>{company.country}</div>}
                      </div>
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Company Details */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground/90">Company Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {company.company_size && (
                    <div className="flex items-start gap-2 text-sm">
                      <Users className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Size:</span>
                        <span className="ml-1 font-medium">{company.company_size}</span>
                      </div>
                    </div>
                  )}
                  {company.company_type && (
                    <div className="flex items-start gap-2 text-sm">
                      <Building className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Type:</span>
                        <span className="ml-1 font-medium capitalize">{company.company_type}</span>
                      </div>
                    </div>
                  )}
                  {company.ownership_type && (
                    <div className="flex items-start gap-2 text-sm">
                      <FileText className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Ownership:</span>
                        <span className="ml-1 font-medium">{company.ownership_type}</span>
                      </div>
                    </div>
                  )}
                  {company.contract_start_date && (
                    <div className="flex items-start gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-primary mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Contract Start:</span>
                        <span className="ml-1 font-medium">{formatDate(company.contract_start_date)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {company.company_overview && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground/90">Overview</h3>
                    <p className="text-sm text-foreground/80">{company.company_overview}</p>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4 mt-0" data-testid="activity-content">
              <ActivityTab entityType="COMPANY" entityId={company?.id} />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4 mt-0" data-testid="notes-content">
              <NotesTab entityType="COMPANY" entityId={company?.id} currentUser={currentUser} />
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose} data-testid="close-modal-btn">
            Close
          </Button>
          {onEdit && canEdit && (
            <Button variant="default" onClick={() => onEdit(company)} data-testid="edit-company-btn">
              Edit Company
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDetailModal;
